import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { parseGestoresEquipeSection } from '@/components/Gestores/gestoresJornadaNavigation'
import { gestoresService, type GestorEquipeDetail, type GestorFeedbackDocumentPayload } from '@/services/gestoresService'

export function useGestorEquipeDetailPage() {
  const { memberId } = useParams<{ memberId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const [detail, setDetail] = useState<GestorEquipeDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!memberId) return
    setLoading(true)
    setError(null)
    try {
      const data = await gestoresService.getEquipeMember(memberId)
      setDetail(data)
    } catch {
      setError('Não foi possível carregar o profissional.')
      setDetail(null)
    } finally {
      setLoading(false)
    }
  }, [memberId])

  useEffect(() => {
    void load()
  }, [load])

  const goBack = useCallback(() => {
    const back = searchParams.get('back')
    navigate(`/gestores${back ? `?${back}` : '?tab=equipe'}`)
  }, [searchParams, navigate])

  const initialSection = parseGestoresEquipeSection(searchParams.get('section'))

  const submitFerias = useCallback(
    async (feriasForm: {
      dataInicio: string
      dataFim: string
      dias: string
      liderancaAprovada: boolean
      observacoes: string
    }) => {
      if (!memberId) return
      await gestoresService.createFerias(memberId, {
        dataInicio: feriasForm.dataInicio,
        dataFim: feriasForm.dataFim,
        dias: Number(feriasForm.dias),
        liderancaAprovada: feriasForm.liderancaAprovada,
        observacoes: feriasForm.observacoes || undefined,
      })
      toast.success('Férias registradas')
      await load()
    },
    [memberId, load],
  )

  const createFeedback = useCallback(
    async (_tipo: GestorFeedbackDocumentPayload['tipo'], payload: GestorFeedbackDocumentPayload) => {
      if (!memberId) return
      await gestoresService.createFeedback(memberId, payload)
      toast.success(payload.tipo === 'ANOTACAO_PRIVADA' ? 'Anotação salva' : 'Registro salvo')
      await load()
    },
    [memberId, load],
  )

  const updateFeedback = useCallback(
    async (feedbackId: string, payload: GestorFeedbackDocumentPayload) => {
      if (!memberId) return
      await gestoresService.updateFeedback(memberId, feedbackId, payload)
      toast.success('Registro atualizado')
      await load()
    },
    [memberId, load],
  )

  const liberarFeedback = useCallback(
    async (feedbackId: string) => {
      if (!memberId) return
      await gestoresService.liberarFeedback(memberId, feedbackId)
      toast.success('Feedback liberado para o profissional')
      await load()
    },
    [memberId, load],
  )

  const deleteFeedback = useCallback(
    async (feedbackId: string) => {
      if (!memberId) return
      await gestoresService.deleteFeedback(memberId, feedbackId)
      toast.success('Registro excluído')
      await load()
    },
    [memberId, load],
  )

  return {
    detail,
    loading,
    error,
    goBack,
    submitFerias,
    createFeedback,
    updateFeedback,
    liberarFeedback,
    deleteFeedback,
    reload: load,
    initialTab: initialSection === 'feedback' ? 'feedback' as const : undefined,
  }
}
